// Inventory (M8, technical design §6.9; the inventory prototype): one office place for the stock on
// every truck and in the warehouse, the morning picklists, and moving stock between them. A side
// list of the parts, as Books has, over the existing actions: stockLocations.list, stock.levels,
// picklists.list / build / get / post, inventory.transfers and inventory.transfer; purchase orders
// (Orders.tsx), counts (Counts.tsx), an item's min, max and primary vendor (ItemSheet.tsx), and
// renaming a truck or the warehouse (RenameLocation.tsx), and returns to vendors (Returns.tsx, BL-013).
// In Blueprint the Stock part is truck stock's approved page (StockPage.tsx, BL-203); Riverstone keeps
// the table below (Stock).

import { useEffect, useState, type ReactNode } from 'react';
import { holdsAny } from '../shared/may.ts';
import { Figure, plural } from '@flowology/ui-web';
import { Link } from '@flowology/ui-web/fl';
import { Header, type NavProps } from '../Nav.tsx';
import { useDesign } from '../blueprint/design.ts';
import { dayText, todayIn } from '../books/model.ts';
import { Empty, Loading, Panel, useAct, useQuery, useToast } from '../books/ui.tsx';
import { newId } from '../shared/ids.ts';
import { scrollFocus } from '../shared/scrollFocus.ts';
import { Counts } from './Counts.tsx';
import { Requests } from './Requests.tsx';
import { StockPage } from './StockPage.tsx';
import { TruckCounts } from './TruckCounts.tsx';
import { ItemSheet } from './ItemSheet.tsx';
import { Orders } from './Orders.tsx';
import { Returns, type ReturnStart } from './Returns.tsx';
import { linesFromPo, RETURN_PERMS } from './returns-model.ts';
import { RenameLocation } from './RenameLocation.tsx';
import { ORDER_PERMS } from './orders-model.ts';
import {
  attentionText,
  leftToUndo,
  momentText,
  movedText,
  nothingAtText,
  oneLocationLede,
  orderLocations,
  PICKLIST_STATUS,
  qtyText,
  rowsAt,
  STOCK_PERMS,
  stockFacts,
  stockRows,
  TRANSFER_REASON,
  validQty,
  WHY_TEXT,
  type Level,
  type StockLocation,
  type StockRow,
  type Transfer,
} from './model.ts';
import '@flowology/ui-web/base.css';
import '../books/books.css';
import '../books/blueprint.css';
import '../shared/office-lists.css';
import './inventory.css';
import './blueprint.css';
import { StockLocationSheet } from '../settings/Setup.tsx';
import { STOCK_BEFORE_THE_SWITCH, useBringingOver } from '../migration/bringing-over.ts';

type Part = 'stock' | 'picklists' | 'transfers' | 'orders' | 'returns' | 'counts' | 'requests';

const PARTS: { part: Part; label: string; title: string; lede: string }[] = [
  {
    part: 'stock',
    label: 'Stock',
    title: 'Stock',
    lede: 'What is on every truck and in the warehouse, against each one’s minimum.',
  },
  {
    part: 'picklists',
    label: 'Picklists',
    title: 'Picklists',
    lede: 'Each truck’s refill from the warehouse for the day: pick it, then post it to move the stock.',
  },
  {
    part: 'transfers',
    label: 'Transfers',
    title: 'Transfers',
    lede: 'Move stock between the warehouse and the trucks. Its cost moves with it.',
  },
  {
    part: 'orders',
    label: 'Purchase orders',
    title: 'Purchase orders',
    lede: 'Draft, send, then receive: what arrives goes into stock at the PO’s cost.',
  },
  {
    part: 'returns',
    label: 'Returns',
    title: 'Returns to vendors',
    lede: 'Parts going back to a vendor: they come off stock when sent, and wait for the vendor’s credit.',
  },
  {
    part: 'counts',
    label: 'Counts',
    title: 'Counts',
    lede: 'Count a truck or the warehouse, or ask a truck’s technician to count it; each difference needs a reason.',
  },
  {
    part: 'requests',
    label: 'Requests',
    title: 'Requests',
    lede: 'What technicians asked for and couldn’t find on their truck: get it, or say why not.',
  },
];

interface Props {
  today: string;
  timeZone?: string;
  toast: (m: string) => void;
  mayManage: boolean;
  /** purchasing.order: raise, change and send purchase orders (and read vendors). */
  mayOrder: boolean;
}

function useLocations() {
  const q = useQuery<{ locations: StockLocation[] }>('stockLocations.list', {});
  return { ...q, ordered: q.data ? orderLocations(q.data.locations) : null };
}

interface StockProps extends Props {
  /** One place's stock alone (a truck, from the board's tech menu), or every place's. */
  focus?: string | null;
  /** Back to every place's. */
  onEvery?: () => void;
  /** The one place shown, once the locations have loaded (the screen's line names it). */
  onFocusShown?: (place: StockLocation | null) => void;
}

function Stock({ toast, mayManage, mayOrder, focus, onEvery, onFocusShown }: StockProps) {
  const blueprint = useDesign() === 'blueprint';
  const locs = useLocations();
  const levels = useQuery<{ levels: Level[] }>('stock.levels', {});
  const place = focus ? (locs.ordered?.find((l) => l.id === focus) ?? null) : null;
  useEffect(() => {
    onFocusShown?.(place);
  }, [place, onFocusShown]);
  // stock starts at the switch: said under the empty table while ServiceTitan comes over (history fix 4)
  const bringingOver = useBringingOver();
  // an item's levels at every location, its min and max, and its primary vendor (ItemSheet)
  const [item, setItem] = useState<{ id: string; name: string } | null>(null);
  const [renaming, setRenaming] = useState<StockLocation | null>(null);
  // stockLocations.create: a warehouse, or a technician's truck (until one exists they have none)
  const [adding, setAdding] = useState(false);
  const add = mayManage && (
    <button className="btn soft" onClick={() => setAdding(true)}>
      Add a truck or warehouse
    </button>
  );
  const sheet = adding && (
    <StockLocationSheet
      onSaved={() => {
        setAdding(false);
        toast('Added. It shows here once it has stock.');
        void locs.reload();
      }}
      onClose={() => setAdding(false)}
    />
  );
  if (!locs.ordered || !levels.data)
    return (
      <Panel title="On hand">
        <Loading
          error={locs.error ?? levels.error}
          what="the stock"
          kind="table"
          cols={['Item', 'Warehouse', 'Trucks']}
          retry={() => void Promise.all([locs.reload(), levels.reload()])}
        />
      </Panel>
    );
  const every = onEvery && <Link onPress={onEvery}>Every location</Link>;
  if (focus && !place)
    // a truck since taken off (or not yet loaded into this list): said, with the way to every place's
    return (
      <Panel title="On hand" aside={every}>
        <Empty>That truck or warehouse isn’t kept any more.</Empty>
      </Panel>
    );
  if (place)
    return (
      <OneLocation
        place={place}
        rows={rowsAt(stockRows(levels.data.levels), place.id)}
        blueprint={blueprint}
        mayManage={mayManage}
        mayOrder={mayOrder}
        locations={locs.ordered}
        every={every}
        toast={toast}
        onChanged={() => void levels.reload()}
      />
    );
  const rows = stockRows(levels.data.levels);
  const attention = attentionText(rows);
  const panel = (
    <Panel
      title="On hand"
      aside={
        attention || add ? (
          <>
            {attention && <span className="chip warn">{attention}</span>}
            {add}
          </>
        ) : undefined
      }
    >
      {sheet}
      {!locs.ordered.length ? (
        <Empty>No warehouse or trucks yet.</Empty>
      ) : !rows.length ? (
        <>
          <Empty>Nothing is stocked yet. Receiving a purchase order puts stock in the warehouse.</Empty>
          {bringingOver && <p className="muted small">{STOCK_BEFORE_THE_SWITCH}</p>}
        </>
      ) : (
        <div className="table-wrap" ref={scrollFocus}>
          <table className="grid-table" aria-label="Stock on hand">
            <thead>
              <tr>
                <th>Item</th>
                {locs.ordered.map((l) => (
                  <th key={l.id} className="num">
                    {mayManage ? (
                      // stockLocations.rename: "Truck 1" becomes "Sam's truck"
                      <button
                        type="button"
                        className="row-btn loc-name"
                        onClick={() => setRenaming(l)}
                        aria-label={`Rename ${l.name}`}
                      >
                        {l.name}
                      </button>
                    ) : (
                      l.name
                    )}
                  </th>
                ))}
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.itemId}>
                  <td>
                    <button
                      type="button"
                      className="row-btn"
                      onClick={() => setItem({ id: r.itemId, name: r.itemName })}
                      aria-label={`${r.itemName}: levels${mayManage ? ', min and max' : ''}`}
                    >
                      {r.itemName}
                    </button>
                  </td>
                  {locs.ordered!.map((l) => {
                    const c = r.cells[l.id];
                    if (!c)
                      return (
                        <td key={l.id} className="num muted">
                          –
                        </td>
                      );
                    return (
                      <td
                        key={l.id}
                        className={`num${c.low ? ' low' : ''}`}
                        title={`min ${qtyText(c.min)} · max ${qtyText(c.max)}`}
                      >
                        {qtyText(c.onHand)}
                        {c.low && <span className="small block">under {qtyText(c.min)}</span>}
                        {c.countNeeded && <span className="small block">to count</span>}
                      </td>
                    );
                  })}
                  <td className="num">
                    <b>{qtyText(r.total)}</b>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {renaming && (
        <RenameLocation
          location={renaming}
          onClose={() => setRenaming(null)}
          onRenamed={(name) => {
            toast(`${renaming.name} is now ${name}.`);
            setRenaming(null);
            void locs.reload();
          }}
        />
      )}
      {item && (
        <ItemSheet
          itemId={item.id}
          itemName={item.name}
          locations={locs.ordered}
          mayManage={mayManage}
          mayVendors={mayOrder}
          onClose={() => setItem(null)}
          onChanged={(said) => {
            toast(said);
            void levels.reload();
          }}
        />
      )}
    </Panel>
  );
  if (!blueprint || !rows.length) return panel;
  // Blueprint (screens/stock.js, Stock levels): what needs someone, as facts over the table
  const f = stockFacts(rows);
  return (
    <>
      <div className="bp-facts flw" role="group" aria-label="The stock today">
        <Figure label="Items stocked" value={String(f.items)} sub={plural(locs.ordered.length, 'place')} />
        <Figure
          label="Low"
          value={<span className={f.low ? 'flw-bad' : undefined}>{f.low}</span>}
          sub="under their minimum"
        />
        <Figure label="To count" value={String(f.toCount)} sub="flagged by a move" />
        {f.belowZero > 0 && (
          <Figure
            label="Below zero"
            value={<span className="flw-bad">{f.belowZero}</span>}
            sub="used but never received"
          />
        )}
      </div>
      {panel}
    </>
  );
}

/**
 * One place's stock (a technician's truck, opened from the board's tech menu): its name as the heading,
 * an item a row with what is there against its minimum and maximum, and the way back to every place's.
 */
function OneLocation({
  place,
  rows,
  blueprint,
  mayManage,
  mayOrder,
  locations,
  every,
  toast,
  onChanged,
}: {
  place: StockLocation;
  /** rowsAt: the items kept there, its cell alone. */
  rows: StockRow[];
  blueprint: boolean;
  mayManage: boolean;
  mayOrder: boolean;
  locations: StockLocation[];
  every: ReactNode;
  toast: (m: string) => void;
  onChanged: () => void;
}) {
  const [item, setItem] = useState<{ id: string; name: string } | null>(null);
  const attention = attentionText(rows);
  const f = stockFacts(rows);
  return (
    <>
      {blueprint && rows.length > 0 && (
        <div className="bp-facts flw" role="group" aria-label={`The stock on ${place.name}`}>
          <Figure label="Items stocked" value={String(f.items)} sub={`on ${place.name}`} />
          <Figure
            label="Low"
            value={<span className={f.low ? 'flw-bad' : undefined}>{f.low}</span>}
            sub="under their minimum"
          />
          <Figure label="To count" value={String(f.toCount)} sub="flagged by a move" />
          {f.belowZero > 0 && (
            <Figure
              label="Below zero"
              value={<span className="flw-bad">{f.belowZero}</span>}
              sub="used but never received"
            />
          )}
        </div>
      )}
      <Panel
        title={place.name}
        aside={
          <>
            {attention && <span className="chip warn">{attention}</span>}
            {every}
          </>
        }
      >
        {!rows.length ? (
          <Empty>{nothingAtText(place)}</Empty>
        ) : (
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table" aria-label={`Stock on ${place.name}`}>
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">On hand</th>
                  <th className="num">Minimum</th>
                  <th className="num">Maximum</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const c = r.cells[place.id]!;
                  return (
                    <tr key={r.itemId}>
                      <td>
                        <button
                          type="button"
                          className="row-btn"
                          onClick={() => setItem({ id: r.itemId, name: r.itemName })}
                          aria-label={`${r.itemName}: levels${mayManage ? ', min and max' : ''}`}
                        >
                          {r.itemName}
                        </button>
                      </td>
                      <td className={`num${c.low ? ' low' : ''}`}>
                        {qtyText(c.onHand)}
                        {c.low && <span className="small block">under {qtyText(c.min)}</span>}
                        {c.countNeeded && <span className="small block">to count</span>}
                      </td>
                      <td className="num">{qtyText(c.min)}</td>
                      <td className="num">{qtyText(c.max)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {item && (
          <ItemSheet
            itemId={item.id}
            itemName={item.name}
            locations={locations}
            mayManage={mayManage}
            mayVendors={mayOrder}
            onClose={() => setItem(null)}
            onChanged={(said) => {
              toast(said);
              onChanged();
            }}
          />
        )}
      </Panel>
    </>
  );
}

/** Purchase orders and counts work over the locations: read once, then the part. */
function WithLocations({
  children,
}: {
  children: (locations: StockLocation[], reload: () => void) => ReactNode;
}) {
  const locs = useLocations();
  if (!locs.ordered)
    return (
      <Panel title="Locations">
        <Loading error={locs.error} what="the locations" retry={() => void locs.reload()} />
      </Panel>
    );
  return <>{children(locs.ordered, () => void locs.reload())}</>;
}

interface PicklistLine {
  itemId: string;
  itemName: string;
  quantity: string;
  why: string;
  shortQty: string;
  pickedQty: string | null;
}
interface PicklistFull {
  id: string;
  locationId: string;
  date: string;
  status: 'open' | 'posted' | 'loaded';
  lines: PicklistLine[];
}

function OnePicklist({
  id,
  name,
  mayManage,
  toast,
  onChanged,
}: {
  id: string;
  name: string;
  mayManage: boolean;
  toast: (m: string) => void;
  onChanged: () => Promise<void>;
}) {
  const q = useQuery<PicklistFull>('picklists.get', { id });
  const [picked, setPicked] = useState<Record<string, string>>({});
  const act = useAct(toast, async () => {
    await Promise.all([q.reload(), onChanged()]);
  });
  if (!q.data) return <Loading error={q.error} what={`${name}’s picklist`} retry={() => void q.reload()} />;
  const p = q.data;
  const open = p.status === 'open' && mayManage;
  const bad = Object.values(picked).some((v) => v.trim() !== '' && !validQty(v) && v.trim() !== '0');
  return (
    <>
      <div className="table-wrap" ref={scrollFocus}>
        <table className="grid-table" aria-label={`${name}’s picklist`}>
          <thead>
            <tr>
              <th>Item</th>
              <th>Why</th>
              <th className="num">To pick</th>
              <th className="num">Short</th>
              <th className="num">{open ? 'Picked' : 'Moved'}</th>
            </tr>
          </thead>
          <tbody>
            {p.lines.map((l) => {
              const short = Number(l.shortQty) > 0;
              return (
                <tr key={l.itemId}>
                  <td>{l.itemName}</td>
                  <td>{WHY_TEXT[l.why] ?? l.why}</td>
                  <td className="num">{qtyText(l.quantity)}</td>
                  <td className={`num${short ? ' low' : ''}`}>{short ? qtyText(l.shortQty) : '–'}</td>
                  <td className="num">
                    {open ? (
                      <input
                        className="field qty-in"
                        inputMode="decimal"
                        aria-label={`Picked ${l.itemName}`}
                        placeholder={qtyText(Number(l.quantity) - Number(l.shortQty))}
                        value={picked[l.itemId] ?? ''}
                        onChange={(e) => setPicked({ ...picked, [l.itemId]: e.target.value })}
                      />
                    ) : (
                      qtyText(l.pickedQty ?? '0')
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {open && (
        <div className="row-actions">
          <button
            className="btn primary"
            disabled={act.busy || bad}
            onClick={() =>
              void act.run(
                'picklists.post',
                {
                  picklistId: p.id,
                  picked: Object.entries(picked)
                    .filter(([, v]) => v.trim() !== '')
                    .map(([itemId, quantity]) => ({ itemId, quantity: quantity.trim() })),
                },
                `${name}’s picklist is posted: the stock moved to the truck.`,
              )
            }
          >
            {act.busy ? 'Posting…' : 'Post the picklist'}
          </button>
          <span className="muted small">
            Leave a line blank to move what the list says (or what there is).
          </span>
        </div>
      )}
      {bad && <p className="error">Picked quantities are numbers, like 3 or 2.5.</p>}
    </>
  );
}

function Picklists({ today, toast, mayManage }: Props) {
  const [date, setDate] = useState(today);
  const q = useQuery<{
    picklists: {
      id: string;
      locationName: string;
      status: 'open' | 'posted' | 'loaded';
      lines: number;
      shortLines: number;
    }[];
  }>('picklists.list', { date });
  const [openId, setOpenId] = useState<string | null>(null);
  const act = useAct(toast, async () => {
    await q.reload();
  });
  return (
    <Panel
      title={`Picklists for ${dayText(date)}`}
      aside={
        <div className="row-actions">
          <label className="inline-label">
            <span className="flabel">Day</span>
            <input
              className="field"
              type="date"
              value={date}
              onChange={(e) => {
                if (e.target.value) setDate(e.target.value);
                setOpenId(null);
              }}
            />
          </label>
          {mayManage && (
            <button
              className="btn"
              disabled={act.busy}
              onClick={() => void act.run('picklists.build', { date }, 'The picklists are built.')}
            >
              {act.busy ? 'Building…' : 'Build the picklists'}
            </button>
          )}
        </div>
      }
    >
      {!q.data ? (
        <Loading error={q.error} what="the picklists" retry={() => void q.reload()} />
      ) : q.data.picklists.length === 0 ? (
        <Empty>
          No picklists for this day yet.
          {mayManage ? ' Build them: each truck gets what it used, or enough to reach its maximum.' : ''}
        </Empty>
      ) : (
        <ul className="cards">
          {q.data.picklists.map((p) => (
            <li key={p.id} className="stone line-card">
              <div className="line-top">
                <div>
                  <b>{p.locationName}</b>
                  <span className="muted small">
                    {p.lines} {p.lines === 1 ? 'line' : 'lines'}
                    {p.shortLines ? `, ${p.shortLines} short in the warehouse` : ''}
                  </span>
                </div>
                <div className="row-actions">
                  <span className={`chip say${p.status === 'open' ? '' : ' ok'}`}>
                    {PICKLIST_STATUS[p.status]}
                  </span>
                  <button
                    className="btn soft"
                    aria-expanded={openId === p.id}
                    onClick={() => setOpenId(openId === p.id ? null : p.id)}
                  >
                    {openId === p.id ? 'Close' : p.status === 'open' && mayManage ? 'Pick it' : 'See it'}
                  </button>
                </div>
              </div>
              {openId === p.id && (
                <OnePicklist
                  id={p.id}
                  name={p.locationName}
                  mayManage={mayManage}
                  toast={toast}
                  onChanged={q.reload}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function MoveForm({
  locations,
  toast,
  onDone,
}: {
  locations: StockLocation[];
  toast: (m: string) => void;
  onDone: () => Promise<void>;
}) {
  const warehouse = locations.find((l) => l.kind === 'warehouse');
  const [from, setFrom] = useState(warehouse?.id ?? locations[0]?.id ?? '');
  const [to, setTo] = useState(locations.find((l) => l.id !== from)?.id ?? '');
  const [lines, setLines] = useState<{ itemId: string; quantity: string }[]>([{ itemId: '', quantity: '' }]);
  const [note, setNote] = useState('');
  const there = useQuery<{ levels: Level[] }>('stock.levels', from ? { locationId: from } : null, [from]);
  const act = useAct(toast, onDone);
  const items = (there.data?.levels ?? []).filter((l) => Number(l.onHand) > 0);
  const toKind = locations.find((l) => l.id === to)?.kind;
  const fromKind = locations.find((l) => l.id === from)?.kind;
  const ok =
    from &&
    to &&
    from !== to &&
    lines.length > 0 &&
    lines.every((l) => l.itemId && validQty(l.quantity)) &&
    new Set(lines.map((l) => l.itemId)).size === lines.length;
  return (
    <form
      className="fgrid"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ok || act.busy) return;
        void act
          .run(
            'inventory.transfer',
            {
              transferId: newId(),
              fromLocationId: from,
              toLocationId: to,
              reason: fromKind === 'truck' && toKind === 'warehouse' ? 'return' : 'transfer',
              lines: lines.map((l) => ({ itemId: l.itemId, quantity: l.quantity.trim() })),
              ...(note.trim() ? { note: note.trim() } : {}),
            },
            'Moved.',
          )
          .then((r) => {
            if (r) {
              setLines([{ itemId: '', quantity: '' }]);
              setNote('');
            }
          });
      }}
    >
      <label>
        <span className="flabel">From</span>
        <select
          className="field"
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            setLines([{ itemId: '', quantity: '' }]);
          }}
        >
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="flabel">To</span>
        <select className="field" value={to} onChange={(e) => setTo(e.target.value)}>
          {locations
            .filter((l) => l.id !== from)
            .map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
        </select>
      </label>
      {lines.map((line, i) => (
        <div key={i} className="wide move-line">
          <label>
            <span className="flabel">Item</span>
            <select
              className="field"
              value={line.itemId}
              onChange={(e) =>
                setLines(lines.map((x, j) => (j === i ? { ...x, itemId: e.target.value } : x)))
              }
            >
              <option value="">{items.length ? 'Pick an item…' : 'Nothing in stock here'}</option>
              {items.map((it) => (
                <option key={it.itemId} value={it.itemId}>
                  {it.itemName} ({qtyText(it.onHand)} here)
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="flabel">Quantity</span>
            <input
              className="field qty-in"
              inputMode="decimal"
              value={line.quantity}
              onChange={(e) =>
                setLines(lines.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))
              }
            />
          </label>
          {lines.length > 1 && (
            <button
              type="button"
              className="btn quiet"
              aria-label={`Remove line ${i + 1}`}
              onClick={() => setLines(lines.filter((_, j) => j !== i))}
            >
              Remove
            </button>
          )}
        </div>
      ))}
      <label className="wide">
        <span className="flabel">Note</span>
        <input
          className="field"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional"
        />
      </label>
      <div className="row-actions">
        <button
          type="button"
          className="btn"
          onClick={() => setLines([...lines, { itemId: '', quantity: '' }])}
        >
          Another item
        </button>
        <button className="btn primary" type="submit" disabled={!ok || act.busy}>
          {act.busy ? 'Moving…' : 'Move the stock'}
        </button>
      </div>
    </form>
  );
}

function Transfers({ toast, mayManage, timeZone }: Props) {
  const locs = useLocations();
  const q = useQuery<{ transfers: Transfer[] }>('inventory.transfers', {});
  // putting a move back (truck-stock parity 5)
  const [undoing, setUndoing] = useState<Transfer | null>(null);
  return (
    <>
      {mayManage && (
        <Panel title="Move stock">
          {!locs.ordered ? (
            <Loading error={locs.error} what="the locations" retry={() => void locs.reload()} />
          ) : locs.ordered.length < 2 ? (
            <Empty>Moving stock needs the warehouse and at least one truck.</Empty>
          ) : (
            <MoveForm locations={locs.ordered} toast={toast} onDone={q.reload} />
          )}
        </Panel>
      )}
      <Panel title="Recent moves">
        {undoing && (
          <UndoMove
            move={undoing}
            toast={toast}
            onClose={() => setUndoing(null)}
            onDone={() => {
              setUndoing(null);
              void q.reload();
            }}
          />
        )}
        {!q.data ? (
          <Loading
            error={q.error}
            what="the moves"
            kind="table"
            cols={['When', 'From', 'To', 'What']}
            retry={() => void q.reload()}
          />
        ) : q.data.transfers.length === 0 ? (
          <Empty>No stock has moved yet.</Empty>
        ) : (
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table recent-moves" aria-label="Recent moves">
              <thead>
                <tr>
                  <th>When</th>
                  {/* on a phone, said under the move's time instead, and its Undo… under what moved (QA-596) */}
                  <th className="wide-col">From</th>
                  <th className="wide-col">To</th>
                  <th className="wide-col">Why</th>
                  <th>What moved</th>
                  {mayManage && <th className="wide-col" />}
                </tr>
              </thead>
              <tbody>
                {q.data.transfers.map((t) => {
                  const undo = mayManage && leftToUndo(t).length > 0 && (
                    <button
                      type="button"
                      className="btn quiet"
                      aria-label={`Undo the move from ${t.fromName} to ${t.toName}, ${momentText(t.occurredAt, timeZone)}`}
                      onClick={() => setUndoing(t)}
                    >
                      Undo…
                    </button>
                  );
                  return (
                    <tr key={t.id}>
                      <td>
                        {momentText(t.occurredAt, timeZone)}
                        <span className="phone-only muted small">
                          {t.fromName} to {t.toName} · {TRANSFER_REASON[t.reason]}
                        </span>
                      </td>
                      <td className="wide-col">{t.fromName}</td>
                      <td className="wide-col">{t.toName}</td>
                      <td className="wide-col">{TRANSFER_REASON[t.reason]}</td>
                      <td>
                        {t.lines.map(movedText).join(', ') || '–'}
                        {t.note && <span className="muted small block">{t.note}</span>}
                        {undo && <span className="phone-only move-undo">{undo}</span>}
                      </td>
                      {mayManage && <td className="wide-col">{undo}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}

/** Putting a move back, all of it or some items (inventory.undoTransfer): only what is left goes back. */
function UndoMove({
  move: t,
  toast,
  onClose,
  onDone,
}: {
  move: Transfer;
  toast: (m: string) => void;
  onClose: () => void;
  onDone: () => void;
}) {
  const left = leftToUndo(t);
  const [picked, setPicked] = useState<string[]>(left.map((l) => l.itemId));
  const [undoId] = useState(newId);
  const act = useAct(toast, () => undefined);
  return (
    <form
      className="fgrid inline-form undo-move"
      aria-label={`Undo the move from ${t.fromName} to ${t.toName}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!picked.length || act.busy) return;
        void act
          .run(
            'inventory.undoTransfer',
            {
              undoId,
              transferId: t.id,
              ...(picked.length < left.length ? { itemIds: picked } : {}),
            },
            `Put back: from ${t.toName} to ${t.fromName}.`,
          )
          .then((r) => r && onDone());
      }}
    >
      <p className="full small">
        Back from {t.toName} to {t.fromName}. If some was used since, {t.toName} goes below zero and is
        flagged for a count.
      </p>
      <ul className="rows full" aria-label="What goes back">
        {left.map((l) => (
          <li key={l.itemId}>
            <label className="checkrow">
              <input
                type="checkbox"
                checked={picked.includes(l.itemId)}
                onChange={(e) =>
                  setPicked(e.target.checked ? [...picked, l.itemId] : picked.filter((x) => x !== l.itemId))
                }
              />{' '}
              {qtyText(l.left)} × {l.itemName}
            </label>
          </li>
        ))}
      </ul>
      <div className="row-actions">
        <button type="submit" className="btn primary" disabled={!picked.length || act.busy}>
          Put them back
        </button>
        <button type="button" className="btn quiet" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function InventoryScreen({
  nav,
  held,
  open,
}: {
  nav: NavProps;
  held: readonly string[];
  /** Stock at one place, asked for from outside (the board's tech menu: their truck); `at` makes each
   * ask a new one. */
  open?: { locationId: string; at: number };
}) {
  const [part, setPart] = useState<Part>('stock');
  // Stock at one place (a truck), until Every location or another part
  const [focus, setFocus] = useState<string | null>(open?.locationId ?? null);
  const [focused, setFocused] = useState<StockLocation | null>(null);
  useEffect(() => {
    if (!open) return;
    setPart('stock');
    setFocus(open.locationId);
  }, [open?.locationId, open?.at]);
  const [returnStart, setReturnStart] = useState<ReturnStart | null>(null);
  const [toast, setToast] = useToast();
  const here = PARTS.find((p) => p.part === part)!;
  // Blueprint's Stock is the approved page (StockPage.tsx, BL-203): one line for every place and one alike
  const blueprint = useDesign() === 'blueprint';
  const lede =
    part === 'stock' && blueprint
      ? 'What is in each place against its minimum.'
      : part === 'stock' && focus && focused
        ? oneLocationLede(focused)
        : here.lede;
  // returns read vendors (vendors.view): purchasing and payables, not the field or the phones
  const parts = PARTS.filter((p) => p.part !== 'returns' || holdsAny(held, RETURN_PERMS));
  const props: Props = {
    today: todayIn(nav.timeZone),
    ...(nav.timeZone ? { timeZone: nav.timeZone } : {}),
    toast: setToast,
    mayManage: holdsAny(held, STOCK_PERMS),
    mayOrder: holdsAny(held, ORDER_PERMS),
  };
  return (
    <div className="app books inventory">
      <Header nav={nav} title={here.title} lede={lede} />
      <div className="books-body">
        <nav className="books-parts" aria-label="Inventory">
          {parts.map((p) => (
            <button
              key={p.part}
              className={`btn quiet${part === p.part ? ' on' : ''}`}
              aria-current={part === p.part ? 'page' : undefined}
              onClick={() => {
                setPart(p.part);
                setFocus(null);
              }}
            >
              {p.label}
            </button>
          ))}
        </nav>
        <main className="books-main">
          <div className="books-page">
            {part === 'stock' && blueprint && (
              <StockPage
                {...props}
                held={held}
                staffId={nav.staffId ?? null}
                focus={focus}
                onFocus={setFocus}
                onCounts={() => {
                  setPart('counts');
                  setFocus(null);
                }}
              />
            )}
            {part === 'stock' && !blueprint && (
              <Stock {...props} focus={focus} onEvery={() => setFocus(null)} onFocusShown={setFocused} />
            )}
            {part === 'picklists' && <Picklists {...props} />}
            {part === 'transfers' && <Transfers {...props} />}
            {part === 'orders' && (
              <WithLocations>
                {(locations) => (
                  <Orders
                    today={props.today}
                    toast={setToast}
                    mayOrder={props.mayOrder}
                    mayReceive={props.mayManage}
                    locations={locations}
                    onReturn={(po) => {
                      setReturnStart({
                        vendorId: po.vendorId,
                        locationId: po.locationId,
                        lines: linesFromPo(po),
                      });
                      setPart('returns');
                    }}
                  />
                )}
              </WithLocations>
            )}
            {part === 'returns' && (
              <WithLocations>
                {(locations) => (
                  <Returns
                    today={props.today}
                    toast={setToast}
                    mayManage={props.mayManage}
                    // the company's return types (returnTypes.save): settings.edit's
                    maySetup={held.includes('settings.edit')}
                    locations={locations}
                    start={returnStart}
                    onStarted={() => setReturnStart(null)}
                  />
                )}
              </WithLocations>
            )}
            {part === 'requests' && <Requests timeZone={props.timeZone} toast={setToast} />}
            {part === 'counts' && (
              <WithLocations>
                {(locations, reload) => (
                  <>
                    {props.mayManage && (
                      <TruckCounts locations={locations} timeZone={props.timeZone} toast={setToast} />
                    )}
                    <Counts
                      locations={locations}
                      toast={setToast}
                      mayCount={props.mayManage}
                      onRenamed={reload}
                    />
                  </>
                )}
              </WithLocations>
            )}
          </div>
        </main>
      </div>
      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}
